import { type CanvasTokenNode } from '../store/slices/createCanvasTokenNodeSlice';
import { findGroupMembers } from '../store/utils/groupUtils';
import { getGroupLabel, getGroupThumbnailUrl } from './mediaFormatUtils';

const GROUP_THUMBNAIL_SIZE = 24;

export const MEDIA_PATTERN = /\[media:([^\]]+)\]/g;

/**
 * Serialize a media reference to its embedded string format.
 */
export function serializeMediaReference(id: string): string {
  return `[media:${id}]`;
}

/**
 * Represents a parsed segment of a message string.
 */
export type MessageSegment =
  | { type: 'text'; value: string }
  | { type: 'media'; id: string; node: CanvasTokenNode }
  | { type: 'group'; groupId: string; memberCount: number; name: string; thumbnailUrl: string | null }
  | { type: 'unavailable' };

/**
 * Parse a message string into segments of text, media references, and unavailable markers.
 *
 * - `[media:id]` patterns are looked up in the provided nodes map.
 *   Found → media segment, not found → unavailable segment.
 * - Legacy `{{token:...}}` patterns are replaced with unavailable segments.
 * - Loose UUIDs in text that match a node ID are converted to media segments.
 * - Everything else is plain text.
 */
export function parseMessageSegments(message: string, nodes: Record<string, CanvasTokenNode>): MessageSegment[] {
  // Replace legacy token patterns with a placeholder so we can handle them uniformly.
  const UNAVAILABLE_PLACEHOLDER = '\0UNAVAILABLE\0';
  const legacyTokenPattern = /\{\{token:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}\}\}/g;
  const withLegacyReplaced = message.replace(legacyTokenPattern, UNAVAILABLE_PLACEHOLDER);

  const segments: MessageSegment[] = [];
  let lastIndex = 0;

  for (const match of withLegacyReplaced.matchAll(MEDIA_PATTERN)) {
    const matchStart = match.index;

    if (matchStart > lastIndex) {
      pushTextWithUnavailable(segments, withLegacyReplaced.slice(lastIndex, matchStart), UNAVAILABLE_PLACEHOLDER);
    }

    const id = match[1];
    const node = nodes[id];

    if (node) {
      segments.push({ id, node, type: 'media' });
    } else {
      // id may be a groupId — check for member nodes that share this group.id
      const members = findGroupMembers(nodes, id);

      if (members.length > 0) {
        const memberNodes = members.map(([, n]) => n);
        const counts = { image: 0, text: 0, video: 0 };
        for (const n of memberNodes) {
          counts[n.type]++;
        }

        segments.push({
          groupId: id,
          memberCount: members.length,
          name: getGroupLabel(counts),
          thumbnailUrl: getGroupThumbnailUrl(memberNodes, GROUP_THUMBNAIL_SIZE),
          type: 'group',
        });
      } else {
        segments.push({ type: 'unavailable' });
      }
    }

    lastIndex = matchStart + match[0].length;
  }

  if (lastIndex < withLegacyReplaced.length) {
    pushTextWithUnavailable(segments, withLegacyReplaced.slice(lastIndex), UNAVAILABLE_PLACEHOLDER);
  }

  // Second pass: scan text segments for loose UUIDs that match known node IDs.
  // This catches bare UUIDs in messages that weren't wrapped in [media:...].
  if (Object.keys(nodes).length > 0) {
    return resolveLooseUuids(segments, nodes);
  }

  return segments;
}

/** Splits a text chunk on unavailable placeholders, pushing text and unavailable segments. */
function pushTextWithUnavailable(segments: MessageSegment[], text: string, placeholder: string): void {
  const parts = text.split(placeholder);

  for (let i = 0; i < parts.length; i++) {
    if (parts[i]) {
      segments.push({ type: 'text', value: parts[i] });
    }

    if (i < parts.length - 1) {
      segments.push({ type: 'unavailable' });
    }
  }
}

/**
 * Scan text segments for standalone UUIDs and replace them with media or unavailable segments.
 * UUIDs already consumed by [media:...] are not in text segments, so no double-processing.
 * Matched UUIDs → media segment; unmatched → unavailable segment.
 */
function resolveLooseUuids(segments: MessageSegment[], nodes: Record<string, CanvasTokenNode>): MessageSegment[] {
  // Capturing group approach avoids lookbehinds, which crash older mobile Safari (pre-iOS 16.4).
  // Group 1: leading whitespace (or empty at start of string). Group 2: the UUID.
  const standaloneUuidPattern = /(^|\s)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\s|$)/gi;
  const result: MessageSegment[] = [];

  for (const segment of segments) {
    if (segment.type !== 'text') {
      result.push(segment);
      continue;
    }

    let lastIndex = 0;
    let hasMatches = false;
    const text = segment.value;

    for (const match of text.matchAll(standaloneUuidPattern)) {
      hasMatches = true;
      const uuid = match[2].toLowerCase();
      const node = nodes[uuid];
      const matchStart = match.index + match[1].length;

      if (matchStart > lastIndex) {
        result.push({ type: 'text', value: text.slice(lastIndex, matchStart) });
      }

      if (node) {
        result.push({ id: uuid, node, type: 'media' });
      } else {
        result.push({ type: 'unavailable' });
      }

      lastIndex = matchStart + match[2].length;
    }

    if (!hasMatches) {
      result.push(segment);
    } else if (lastIndex < text.length) {
      result.push({ type: 'text', value: text.slice(lastIndex) });
    }
  }

  return result;
}
